// Phase-3 Slice 34 — tab courtesy notice store.
//
// PRD: Ralph/NavigationalFeatures/PRD.md § Slice 34.
//
// Holds the per-tab list of `TabCourtesyNotice`s emitted by
// `scanTabsForRenamedIdentifier` after a successful rename. The
// `<RenameTabNotice />` component subscribes per-tab. Dismissal removes the
// notice from the store; persistence is session-only (cleared on reload by
// virtue of the store living in module memory only).

import { create } from 'zustand';

import type { QualifiedName, TabCourtesyNotice } from '../types';

type State = {
  /** tabId → notices, in receive order. Latest renames append at the end. */
  readonly byTabId: Readonly<Record<string, readonly TabCourtesyNotice[]>>;
};

type Actions = {
  readonly publishNotices: (notices: readonly TabCourtesyNotice[]) => void;
  readonly dismissNotice: (
    tabId: string,
    oldName: QualifiedName,
    newName: QualifiedName,
  ) => void;
  readonly clearForTab: (tabId: string) => void;
  readonly clearAll: () => void;
};

const initial: State = { byTabId: {} };

export const useRenameTabNoticeStore = create<State & Actions>((set) => ({
  ...initial,

  publishNotices: (notices) => {
    if (notices.length === 0) return;
    set((state) => {
      const next: Record<string, readonly TabCourtesyNotice[]> = { ...state.byTabId };
      for (const notice of notices) {
        const existing = next[notice.tabId] ?? [];
        // Dedupe by (oldName, newName) so re-publishing the same rename
        // (e.g. the user re-runs the dialog) doesn't stack identical
        // banners. Match on folded names — the notice carries the
        // QualifiedNames produced by the planner so foldedName is set.
        const filtered = existing.filter(
          (n) =>
            keyForNotice(n) !== keyForNotice(notice),
        );
        next[notice.tabId] = [...filtered, notice];
      }
      return { byTabId: next };
    });
  },

  dismissNotice: (tabId, oldName, newName) => {
    set((state) => {
      const existing = state.byTabId[tabId];
      if (!existing) return state;
      const target = `${nameKey(oldName)}→${nameKey(newName)}`;
      const remaining = existing.filter(
        (n) => `${nameKey(n.oldName)}→${nameKey(n.newName)}` !== target,
      );
      const next = { ...state.byTabId };
      if (remaining.length === 0) delete next[tabId];
      else next[tabId] = remaining;
      return { byTabId: next };
    });
  },

  clearForTab: (tabId) => {
    set((state) => {
      if (!(tabId in state.byTabId)) return state;
      const next = { ...state.byTabId };
      delete next[tabId];
      return { byTabId: next };
    });
  },

  clearAll: () => set(initial),
}));

function keyForNotice(n: TabCourtesyNotice): string {
  return `${nameKey(n.oldName)}→${nameKey(n.newName)}`;
}

function nameKey(qn: QualifiedName): string {
  const db = (qn.foldedDatabase ?? '').toLowerCase();
  const schema = (qn.foldedSchema ?? '').toLowerCase();
  const name = (qn.foldedName ?? qn.name ?? '').toLowerCase();
  return `${db}::${schema}::${name}`;
}
